import { capitalize } from 'vue';

import DatadogApi from '@/common/apis/DatadogApi';
import GoogleAnalyticsApi, { GoogleAnalyticsEventCategory } from '@/common/apis/GoogleAnalyticsApi';
import Environment from '@/common/enums/environment.enum';
import Organization from '@/common/models/memberships/Organization';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { environment } from '@/globals';
import rootStore from '@/store';
import LocalCache from '@/utils/LocalCache';
import UserNotification, { UserNotificationType } from '@/utils/UserNotification';
import i18n from '@/vueI18n';

interface INotificationPayload {
  title: string;
  message: string;
  link?: string;
  details?;
  actions?;
  category?;
  duration?: number;
}

type NotificationTypes = keyof typeof UserNotificationType;

export default class UserNotifications {
  static notify(options: INotificationPayload & { type: NotificationTypes }) {
    // See Notification's constructor for options.
    const notification = new UserNotification(options);
    GlobalEventBus.$emit(GlobalEvents.notify, notification);
  }

  static clear(category = null) {
    // See Notification's constructor for options.
    GlobalEventBus.$emit(GlobalEvents.clearNotifications, category);
  }

  static info(notification: INotificationPayload) {
    UserNotifications.notify({
      type: UserNotificationType.info,
      ...notification,
    });
  }

  static success(notification: INotificationPayload) {
    UserNotifications.notify({
      type: UserNotificationType.success,
      ...notification,
    });
  }

  static error(notification: INotificationPayload, sendToAnalytics = true) {
    const response = notification.details ? JSON.parse(notification.details.content) : null;
    const description = response?.config
      ? `${response.config.url}|${response.config.method.toUpperCase()}|${response.status}`
      : undefined;

    //
    // Send error event to Google Analytics
    //
    if (sendToAnalytics) {
      GoogleAnalyticsApi.sendEvent(GoogleAnalyticsEventCategory.ErrorNotification, {
        label: notification.message,
        description,
        value: response?.status,
      });
    }
    UserNotifications.notify({
      type: UserNotificationType.error,
      ...notification,
    });

    //
    // Send error event to Datadog
    //
    if (sendToAnalytics) {
      const org = LocalCache.get('organization');
      const orgID = org != null ? Organization.fromApi(org).id : null;

      DatadogApi.sendEvent(GoogleAnalyticsEventCategory.ErrorNotification, {
        environment,
        organization_id: orgID,
        user_id: rootStore.getters.user?.id,
        page: window.location.pathname,
        url: response?.config ? response.config.url : 'N/A',
        method: response?.config ? response.config.method.toUpperCase() : 'N/A',
        status: response?.status,
      });
    }
  }

  static warning(notification: INotificationPayload) {
    UserNotifications.notify({
      type: UserNotificationType.warning,
      ...notification,
    });
  }

  static somethingWentWrong(e = null, message: string = null) {
    UserNotifications.error({
      title: i18n.global.t('messages.somethingWentWrong'),
      message,
      details: {
        label: i18n.global.t('messages.viewErrorReport'),
        modalTitle: capitalize(i18n.global.t('names.errorReport', { n: 1 })),
        content: JSON.stringify(e, null, 2),
        url: e?.response?.url,
      },
    });
  }

  static unexpectedChangeDetected() {
    // Only show this notification in non-production environments.
    if (environment === Environment.PROD) return;
    UserNotifications.error({
      title: capitalize(i18n.global.t('messages.unexpectedChangeDetectedTitle')),
      message: i18n.global.t('messages.unexpectedChangeDetectedDescription'),
      duration: 30000,
    });
  }
}
